import React, { useMemo, useCallback, useEffect, useState, useContext } from 'react';
import {
  ReactFlow,
  Background,
  Controls,
  addEdge,
  applyNodeChanges,
  Connection,
  type Node,
  NodeChange,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { EdgeBase } from '@xyflow/system';
import { useNavigate, useParams } from 'react-router';
import { PlayIconFlow, UploadFile } from '../../../../assets/images';
import { deleteActionWorkflow, getWorkflowData, publishWorkFlow } from '../services';
import makeToast from '../../../../components/toaster';
import {
  DisableWorkflowContext,
  WorkFlowActionContext,
  WorkFlowContext,
} from '../../../../utils/context';
import { BreadCrumbs, LoaderOverlay } from '../../../../components';
import { initialEdges, initialNodes } from '../data/constants';
import { CustomEdge, CustomEnd, CustomNode } from '../config/nodes.config';
import { customEdgeProps, customNodeProps, workflowDiagramProps } from '../automator.props';

function WorkflowDiagram({
  setSelectedNode,
  setNodes,
  setEdges,
  onEdgesChange,
  edges,
  nodes,
  selectedNode,
  setOpenPanel,
  setActiveTab,
  setLogout,
}: workflowDiagramProps) {
  const navigate = useNavigate();
  const { projectId, workflowId } = useParams();
  const { setWorkFlowData } = useContext(WorkFlowContext);

  const { disableMode } = useContext(DisableWorkflowContext);
  const { setWorkFlowActionData } = useContext(WorkFlowActionContext);
  const [existingWorkflowData, setExistingWorkflowData] = useState<{
    data?: object & { id: string }[];
    id: number;
    name: string;
  } | null>(null);
  const [loader, setLoader] = useState<boolean>(false);
  const [isActionsOpen, setIsActionsOpen] = useState<Record<string, boolean>>({});
  const nodeRefs = useMemo(() => new Map<string, React.RefObject<HTMLDivElement>>(), []);

  useEffect(() => {
    // Attach refs for each node
    nodes.forEach((node) => {
      if (!nodeRefs.has(node.id)) {
        nodeRefs.set(node.id, React.createRef<HTMLDivElement>());
      }
    });
  }, [nodes, nodeRefs]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node | null; // Type assertion

      if (!(target instanceof Node)) return; // Ensure target is a valid Node
      let clickedInsideAnyNode = false;

      nodeRefs.forEach((ref) => {
        if (ref.current && ref.current.contains(target)) {
          clickedInsideAnyNode = true;
        }
      });

      if (!clickedInsideAnyNode) {
        setIsActionsOpen({}); // Close all menus if clicked outside
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [nodeRefs]);

  interface WorkflowData {
    title: string;
    action_name: string;
    trigger_name: string;
    action_id: number;
    trigger_id: number;
    trigger_app: string;
  }

  const mapWorkflowDataToExistingFormat = (workflowData: WorkflowData[]) =>
    workflowData.map((data: WorkflowData, index: number) => ({
      id: `${index + 1}`, // Mapping `trigger_id` to `id`
      type: 'custom',
      data: {
        icon: '/static/media/flashIcon.5d100f9f1940a0432b7d09393a904af9.svg', // Placeholder for icon as it isn't provided in `workflowData`
        title: (data?.title || data?.action_name) ?? 'Select Action', // Mapping `trigger_app` to `data.title`
        description: data.trigger_name ?? 'Empty action', // Mapping `trigger_name` to `data.description`
        selected: index === 0, // Default value for `selected`
        unique_id: data.action_id || data.trigger_id,
        trigger_type: data.trigger_app || data?.title || data?.action_name, // Placeholder for `trigger_type` as it isn't provided
      },
      position: {
        x: 50, // Default position values
        y: index * 150, // Staggering the positions vertically for layout
      },
      measured: {
        width: 300, // Default width
        height: 100, // Default height
      },
    }));

  useEffect(() => {
    if (workflowId && projectId) {
      getWorkflowData({
        project_id: projectId,
        workflow_id: workflowId,
      })
        ?.then((res) => {
          const allTriggerResponses = res?.data?.data?.trigger;
          const allActionResponse = res?.data?.data?.actions;
          if (res?.status === 200) {
            const { data } = res.data || {};
            const { trigger, actions, id, name } = data || {};
            const triggerData = trigger?.[0];

            if (!trigger?.length) {
              setNodes(initialNodes);
              setEdges(initialEdges);
              return;
            }

            const workflowNodes = actions?.length
              ? [...trigger, ...actions]
              : [...trigger, initialNodes[1]];

            setExistingWorkflowData({
              data: mapWorkflowDataToExistingFormat(workflowNodes),
              id,
              name,
            });

            localStorage.setItem(
              'workFlowData',
              JSON.stringify({
                trigger: {
                  ...triggerData,
                  data: { ...triggerData?.data, node_type: 'trigger' },
                  isSetUpTabCompleted: true,
                  isConfigTabCompleted: true,
                  isTestTabCompleted: true,
                },
                actions:
                  actions?.length > 0
                    ? actions.map((item: { action_order: number; data: object }) => ({
                        ...item,
                        isSetUpTabCompleted: true,
                        isConfigTabCompleted: true,
                        isTestTabCompleted: true,
                        action_order: item.action_order + 1,
                        data: { ...item.data, node_type: 'action' },
                      }))
                    : [
                        {
                          ...initialNodes[1],
                          action_order: '2',
                        },
                      ],
              }),
            );

            setWorkFlowData((prev) => ({
              ...prev,
              trigger_catalogue_id: `${triggerData?.trigger_catalogue_id}`,
              workFlowData: { trigger_config: triggerData?.trigger_config },
            }));

            const mappedResponses = [
              ...(allTriggerResponses || []).map(
                ({
                  trigger_id: triggerId,
                  execution_id: executionId,
                  response,
                }: {
                  trigger_id: number;
                  execution_id: number;
                  response: object;
                }) => ({
                  previous_id: triggerId,
                  execution_id: executionId,
                  response,
                }),
              ),

              ...(allActionResponse || []).map(
                ({
                  action_id: actionId,
                  execution_id: executionId,
                  response,
                }: {
                  action_id: number;
                  execution_id: number;
                  response: object;
                }) => ({
                  previous_id: actionId,
                  executionId,
                  response,
                }),
              ),
            ];
            localStorage.setItem('triggerResponse', JSON.stringify(mappedResponses));
          } else if (res?.status === 401) {
            setLogout(true);
          }
        })
        .catch(() => {
          makeToast('error', 'Error fetching workflow data');
        });
    }
  }, []);

  useEffect(() => {
    if (selectedNode?.data?.trigger_type) {
      setNodes((nds) =>
        nds.map((node) =>
          node.id === selectedNode.id
            ? {
                ...node,
                data: {
                  ...node?.data,
                  trigger_type: selectedNode?.data?.trigger_type,
                  description: selectedNode?.data?.description ?? 'Select An Action',
                },
              }
            : node,
        ),
      );
    }
  }, [selectedNode, setNodes]);

  useEffect(() => {
    if (workflowId && projectId && (existingWorkflowData?.data?.length ?? 0) > 0) {
      // Map and align the nodes vertically
      const alignedNodes: {
        id: string;
        position: { x: number; y: number };
        data: { description?: string };
      }[] = (
        existingWorkflowData?.data as {
          id: string;
          position: { x: number; y: number };
          data: { description?: string };
        }[]
      ).map((node, index) => ({
        ...node,
        id: node.id,
        position: {
          x: 175, // Center horizontally
          y: index * 150, // Vertically stagger nodes by 150px
        },
        data: node.data || { description: '' }, // Ensure data property is included
      })) as { id: string; position: { x: number; y: number }; data: { description?: string } }[];

      // Add the endNode at the bottom
      const endNode = {
        id: 'end',
        type: 'end',
        data: {
          title: 'End',
          description: 'End node',
        },
        position: {
          x: 300,
          y: alignedNodes.length * 170, // Position after the last node
        },
        selected: false,
        measured: {
          width: 48,
          height: 34,
        },
      };

      // Generate edges connecting all nodes
      const newEdges = alignedNodes?.reduce(
        (
          acc: EdgeBase<Record<string, unknown>, string | undefined>[],
          node: { id: string },
          index: number,
        ) => {
          if (index < (alignedNodes?.length ?? 0) - 1) {
            acc.push({
              id: `edge-${node.id}-${alignedNodes[index + 1]?.id}`,
              source: node.id,
              target: alignedNodes[index + 1].id,
              type: 'custom',
            });
          }
          return acc;
        },
        [],
      );

      // ✅ Fix: Ensure there's at least one node before connecting to 'end'
      if (alignedNodes.length > 0) {
        const lastNode = alignedNodes[alignedNodes.length - 1];
        newEdges.push({
          id: `edge-${lastNode.id}-end`,
          source: lastNode.id,
          target: 'end',
          type: 'custom',
        });
      }

      // Set the nodes and edges
      setNodes([...(alignedNodes ?? []), endNode]);
      setEdges(newEdges);
    }
  }, [workflowId, projectId, existingWorkflowData?.data]);

  const onAddNode = useCallback(
    (source: string, target: string) => {
      const res = localStorage.getItem('triggerResponse');
      const savedValue = localStorage?.getItem('workFlowData');
      const parsedValue = savedValue ? JSON.parse(savedValue) : {};
      // const selectActionConfigIndex =
      //   parsedValue?.actions[(parsedValue?.actions?.length ?? 0) - 1]?.action_order;
      const selectActionConfigIndex =
        parsedValue?.actions[(parsedValue?.actions?.length ?? 0) - 1]?.action_order;
      const triggerResponse = res ? JSON.parse(res) : [];
      // Ensure the responses match the current nodes
      if (triggerResponse.length !== nodes.length - 1) {
        makeToast('error', 'Please complete previous step!');
        return;
      }

      // Find the target and source nodes
      const targetNode = nodes.find((node) => node.id === target);
      const sourceNode = nodes.find((node) => node.id === source);

      if (!targetNode || !sourceNode) return;
      // Ensure unique ID
      const newNodeId = `${Number(selectActionConfigIndex) + 1}`;
      // **Calculate new y position to avoid overlapping**
      const maxY = Math.max(...nodes.map((node) => node.position.y));
      const newY = maxY; // Always places below the lowest node

      // Create a new node
      const newNode = {
        id: newNodeId,
        type: 'custom',
        order: Number(selectActionConfigIndex) + 1,
        data: {
          icon: PlayIconFlow,
          title: 'Select Action',
          description: 'Empty Action',
        },
        position: {
          x: sourceNode.position.x,
          y: newY, // Ensures it doesn't overlap
        },
      };

      // **Slide down nodes below the target node**
      const updatedNodes = nodes.map((node) => {
        if (node.position.y >= targetNode.position.y) {
          return {
            ...node,
            position: {
              ...node.position,
              y: node.position.y + 150,
            },
          };
        }
        return node;
      });

      // Create new edges
      const newEdges = edges
        .filter((edge) => edge.source !== source || edge.target !== target) // Remove original edge
        .concat([
          {
            id: `e${source}-${newNode.id}`,
            source,
            target: newNode.id,
            type: 'custom',
          },
          {
            id: `e${newNode.id}-${target}`,
            source: newNode.id,
            target,
            type: 'custom',
          },
        ]);

      // **Update state**
      setNodes([...updatedNodes, newNode]);
      setEdges(newEdges);
    },
    [nodes, edges],
  );

  const onNodesChange = useCallback(
    (changes: NodeChange<Node>[]) => setNodes((nds) => applyNodeChanges(changes, nds as Node[])),
    [setNodes],
  );

  const onNodeClick = useCallback(
    (_event: unknown, node: Node) => {
      if (node?.type === 'end') {
        makeToast('info', 'End node cannot be selected');
        return;
      }
      setOpenPanel?.(true);
      setNodes((nds) =>
        nds.map((n) => ({
          ...n,
          data: {
            ...n.data,
            selected: n.id === node.id, // Mark the selected node
            nodeComplete: n.id === node.id,
            node_type: n.id === '1' ? 'trigger' : 'action',
          },
        })),
      );
      const selNode = {
        ...node,
        data: {
          ...node?.data,
          selected: true,
          node_type: node.id === '1' ? 'trigger' : 'action',
          unique_id: node.data.unique_id as number,
          trigger_type: node.data.trigger_type as string,
        },
      };
      setSelectedNode(node.type !== 'end' ? selNode : null);
      if (selNode) {
        const wfId =
          workflowId ||
          JSON.parse(localStorage.getItem('workFlowData') || '{}')?.trigger?.workflow_id;
        const savedValue = localStorage?.getItem('workFlowData');
        const parsedSavedValue = JSON.parse(savedValue!);
        const selectActionConfig = parsedSavedValue?.actions?.filter(
          (item: { action_order: string; action_id: number }) =>
            item.action_order === selNode.id ||
            (item.action_id && item.action_id === selNode.data.unique_id),
        )?.[0];
        setWorkFlowActionData({
          ...selectActionConfig,
          project_id: projectId,
          workflow_id: wfId,
        });
      }
      setActiveTab?.(0);
      setIsActionsOpen({});
    },
    [setNodes],
  );

  const edgeTypes = useMemo(
    () => ({
      custom: (props: customEdgeProps) => <CustomEdge {...props} onAddNode={onAddNode} />,
    }),
    [onAddNode],
  );

  const toggleActions = (nodeId?: string | number, value?: boolean) => {
    setIsActionsOpen({});
    setIsActionsOpen((prev) => ({
      ...prev,
      [nodeId as string]: value !== undefined ? value : !prev[nodeId as string], // Toggle or explicitly set the state
    }));
  };

  const handleDeleteNode = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    nodeId: string,
    actionId?: string,
  ) => {
    e.stopPropagation();
    if (disableMode) return;
    // Find the node index
    const nodeIndex = nodes.findIndex((node) => node.id === nodeId);

    // Prevent deletion of the node at index 0
    if (nodeIndex === 0) {
      makeToast('error', 'The first node cannot be deleted!');
      return;
    }

    // Ensure there are enough nodes to keep the workflow valid
    if ((nodes?.length ?? 0) - 1 > 2) {
      // Find the node index
      const wfId =
        workflowId ||
        JSON.parse(localStorage.getItem('workFlowData') || '{}')?.trigger?.workflow_id;
      if (actionId) {
        deleteActionWorkflow({ workflow_id: wfId, actionId })
          .then((res) => {
            makeToast('success', res?.data?.message);
          })
          .catch(() => {
            makeToast('error', 'Something went Wrong!');
          });
      }
      const savedData = localStorage.getItem('workFlowData');
      const parsedData = JSON.parse(savedData!);
      parsedData.actions = parsedData.actions?.filter(
        (item: { action_order: string }) => !(item.action_order === nodeId),
      );
      localStorage.setItem('workFlowData', JSON.stringify(parsedData));
      const nodeIndexVal = nodes.findIndex((node) => node.id === nodeId);
      // Update localStorage: Remove the response at the corresponding index
      const storedResponses = JSON.parse(localStorage.getItem('triggerResponse') || '[]');

      if (nodeIndexVal >= 0 && nodeIndexVal <= storedResponses.length) {
        storedResponses.splice(nodeIndexVal - 1, 1); // Adjust for index starting at 1
        localStorage.setItem('triggerResponse', JSON.stringify(storedResponses));
      }
      // Find the edges connected to the deleted node
      const incomingEdge = edges.find((edge) => edge.target === nodeId);
      const outgoingEdge = edges.find((edge) => edge.source === nodeId);

      // If both incoming and outgoing edges exist, create a new edge connecting them
      if (incomingEdge && outgoingEdge) {
        const newEdge = {
          id: `e${incomingEdge.source}-${outgoingEdge.target}`,
          source: incomingEdge.source,
          target: outgoingEdge.target,
          type: 'custom',
        };

        // Add the new edge to the edges list
        setEdges((prevEdges: EdgeBase[]) => [
          ...prevEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId), // Remove edges connected to the deleted node
          newEdge, // Add the new edge connecting the remaining nodes
        ]);
      } else {
        // Remove edges connected to the deleted node if no valid reconnection exists
        setEdges((prevEdges: EdgeBase[]) =>
          prevEdges.filter((edge) => edge.source !== nodeId && edge.target !== nodeId),
        );
      }

      // Remove the deleted node
      setNodes((prevNodes) => prevNodes.filter((node) => node.id !== nodeId));

      // Close the actions menu after deletion
      setIsActionsOpen({});
    }
  };

  const nodeTypes = useMemo(
    () => ({
      custom: (props: customNodeProps) => {
        return (
          <CustomNode
            {...props}
            isActionsOpen={isActionsOpen[props.id as number] || false}
            toggleActions={toggleActions}
            nodeRef={nodeRefs.get(props.id as string)}
            handleDeleteNode={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) =>
              handleDeleteNode(e, props.id as string, props?.data?.unique_id)
            }
            nodes={nodes}
          />
        );
      },
      end: CustomEnd,
    }),
    [isActionsOpen],
  );

  const handlePublishClick = () => {
    setLoader(true);
    const wfId =
      workflowId || JSON.parse(localStorage.getItem('workFlowData') || '{}')?.trigger?.workflow_id;
    publishWorkFlow({ workflow_id: wfId })
      ?.then((res) => {
        if (res.status === 200) {
          makeToast('success', 'Workflow published successfully');
          navigate('/automator');
          setLoader(false);
        } else if (res?.status === 401) {
          setLogout(true);
        }
      })
      .catch(() => {
        setLoader(false);
        makeToast('error', 'Error publishing workflow');
      });
  };

  return (
    <>
      <div className="flex justify-between pb-3 mb-4 border-b cursor-default">
        <BreadCrumbs />
        <button
          type="button"
          className="flex gap-x-2 items-center border-2 text-[#122961] border-[#122961] py-2 px-[18px] rounded-lg"
          onClick={handlePublishClick}
        >
          <img src={UploadFile} className="text-sm" alt="upload" />
          <span className="text-[#122961] font-bold">Publish</span>
        </button>
      </div>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        onNodesChange={onNodesChange}
        onEdgesChange={onEdgesChange}
        onConnect={(params: Connection) => setEdges((eds: EdgeBase[]) => addEdge(params, eds))}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        onNodeClick={onNodeClick}
        onPaneClick={() => setIsActionsOpen({})}
      >
        <Controls />
        <Background color="#aaa" gap={16} />
      </ReactFlow>
      {loader && <LoaderOverlay />}
    </>
  );
}

export default WorkflowDiagram;
